import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { FileText, Image as ImageIcon, UploadCloud, CheckCircle2, X, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { extractPdfText } from "../../lib/pdf";

// Sobe 1 arquivo (PDF → extração local + /materials/text; imagem → /materials/image com OCR).
// A lógica de extração/OCR do backend não muda — isso aqui é só o caminho até ela.
export async function uploadMaterialFile(file, conversationId) {
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")) {
    const text = await extractPdfText(file);
    const { data } = await api.post("/materials/text", {
      conversation_id: conversationId,
      filename: file.name || "material.pdf",
      text,
    });
    return data;
  }
  if ((file.type || "").startsWith("image/")) {
    const fd = new FormData();
    fd.append("conversation_id", conversationId);
    fd.append("file", file);
    const { data } = await api.post("/materials/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  }
  throw new Error("Formato não suportado. Manda PDF ou foto (JPG/PNG).");
}

const READING_MSGS = [
  "Lendo seu material...",
  "Separando os tópicos importantes...",
  "Guardando isso na memória do tutor...",
];

// Animação de processamento estilo NotebookLM: documento com linha de "scan",
// linhas de texto que acendem e partículas subindo pro ícone do tutor.
export function ProcessingCard({ filename, stage = "reading", onDismiss }) {
  const reduced = useReducedMotion();
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    if (stage !== "reading") return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % READING_MSGS.length), 1800);
    return () => clearInterval(t);
  }, [stage]);

  const done = stage === "done";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      className="relative overflow-hidden rounded-2xl border border-violet-500/40 bg-violet-500/[0.07] p-4 flex items-center gap-4"
      data-testid="material-processing"
    >
      {/* Documento sendo escaneado */}
      <div className="relative h-16 w-12 shrink-0 rounded-md border border-violet-400/40 bg-card overflow-hidden">
        <div className="absolute inset-x-1.5 top-2 space-y-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.div
              key={i}
              className="h-1 rounded-full bg-violet-400/30"
              style={{ width: `${90 - i * 12}%` }}
              animate={done || reduced ? { opacity: 1, backgroundColor: "rgba(52,211,153,0.5)" } : { opacity: [0.25, 1, 0.25] }}
              transition={done || reduced ? { duration: 0.4 } : { duration: 1.6, repeat: Infinity, delay: i * 0.25 }}
            />
          ))}
        </div>
        {!done && !reduced && (
          <motion.div
            className="absolute inset-x-0 h-5 bg-gradient-to-b from-transparent via-violet-400/50 to-transparent"
            animate={{ top: ["-20%", "110%"] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        {done && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className="absolute inset-0 flex items-center justify-center bg-emerald-500/15"
          >
            <CheckCircle2 className="h-6 w-6 text-emerald-400" />
          </motion.div>
        )}
      </div>

      {/* Partículas convergindo pro tutor */}
      {!done && !reduced && (
        <div className="pointer-events-none absolute left-14 top-1/2 h-0 w-24">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="absolute h-1 w-1 rounded-full bg-violet-400"
              animate={{ x: [0, 90], y: [i * 8 - 8, 0], opacity: [0, 1, 0], scale: [0.6, 1, 0.4] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.4, ease: "easeIn" }}
            />
          ))}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{filename}</p>
        <AnimatePresence mode="wait">
          <motion.p
            key={done ? "done" : msgIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className={`text-xs mt-0.5 ${done ? "text-emerald-300" : "text-violet-300"}`}
          >
            {done ? "Pronto! Já posso usar isso nas respostas, quiz e flashcards ✨" : READING_MSGS[msgIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="shrink-0">
        {done ? (
          <button onClick={onDismiss} className="rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        ) : (
          <Sparkles className="h-5 w-5 text-violet-400 animate-pulse" />
        )}
      </div>
    </motion.div>
  );
}

// Dropzone grande e óbvia: arrasta, clica ou cola print.
export function MaterialDropzone({ onFiles, busy = false, compact = false }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  return (
    <div
      data-testid="material-dropzone"
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!busy && e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
      }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed transition flex flex-col items-center justify-center text-center gap-2 ${
        compact ? "p-4" : "p-8"
      } ${over ? "border-violet-400 bg-violet-500/10 scale-[1.01]" : "border-border bg-secondary hover:border-violet-500/50"}`}
    >
      <div className={`rounded-2xl bg-violet-500/15 ${compact ? "p-2.5" : "p-3.5"}`}>
        <UploadCloud className={`text-violet-400 ${compact ? "h-6 w-6" : "h-8 w-8"}`} strokeWidth={1.5} />
      </div>
      <p className={`font-semibold tracking-tight ${compact ? "text-sm" : ""}`}>
        Arraste seu PDF, foto da matéria ou material aqui
      </p>
      <p className="text-xs text-muted-foreground">
        ou <span className="text-violet-400 font-medium">clique pra escolher</span> · colar print com Ctrl+V também funciona
      </p>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground/70 mt-1">
        <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> PDF</span>
        <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Foto / print (OCR)</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// Card/chip do material anexado, com remover
export function MaterialChip({ material, onRemove }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 pl-2.5 pr-1.5 py-1 text-xs max-w-[220px]"
      data-testid={`material-chip-${material.id}`}
    >
      {material.kind === "image" ? (
        <ImageIcon className="h-3 w-3 text-violet-400 shrink-0" />
      ) : (
        <FileText className="h-3 w-3 text-violet-400 shrink-0" />
      )}
      <span className="truncate text-violet-200">{material.filename}</span>
      {onRemove && (
        <button
          onClick={() => onRemove(material.id)}
          className="rounded-full p-0.5 text-muted-foreground hover:text-red-400 hover:bg-background/40"
          title="Remover material"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </motion.div>
  );
}
